import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import axios, { AxiosError } from 'axios';
import Cookie from 'js-cookie';
import * as Sentry from '@sentry/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { dequal } from 'dequal';
import {
  IconLoader2,
  IconSquareRoundedNumber1Filled,
  IconSquareRoundedNumber2Filled,
  IconSquareRoundedNumber3Filled,
  IconDeviceFloppy,
  IconCircleCheck,
  IconSquareRoundedNumber4Filled,
  IconSquareRoundedNumber5Filled,
  IconAlertCircle,
} from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import useChatStore from 'chat-ui/src/store';
import { toast } from 'react-toastify';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { getErrorMessage } from '@common/utils';
import { MessageCircle } from 'lucide-react';
import { EProjectDesignSection, EProjectStatus, EStatusCode } from '@/enums';
import DashboardTestChat from '@/components/chat/DashboardTestChat';
import useMediaQuery from '@/hooks/useMediaQuery';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import Context from '@/components/projects/single-project/research-design/Context';
import Instructions from '@/components/projects/single-project/research-design/Instructions';
import { DiscussionGuide } from '@/components/projects/single-project/research-design/DiscussionGuide';
import { Skeleton } from '@/components/ui/skeleton';
import { LangContext } from '@/context/LangProvider';
import useErrorStore from '@/store/ErrorStore';
import TestChatSkeletonLoading from './DashboardTestChatSkeletonLoading';
import { useMutateProjectUpdate } from '@/reactQuery/post';
import { Button } from '@/components/ui/button';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { useMeasuredHeight, collapsibleMaxHeight } from '@/hooks/useMeasuredHeight';
import useCreateProjectStore from '@/store/CreateProjectStore';
import UnsavedChangesModal from '@/components/Modals/UnsavedChangesModal';
import { getSaveButtonIcon, useFormButtonText } from '@/hooks/useFormButtonText';
import ScreeningQuestions from '@/components/projects/single-project/research-design/ScreeningQuestions';
import useProjectRequiredFieldsStore, {
  computeRequiredFields,
} from '@/store/ProjectRequiredFieldsStore';
import {
  ProjectCreationValues,
  QuestionsFilesData,
  ScreeningQuestion,
  InterviewQuestions,
} from '@/types';
import { useGetSingleProjectData } from '@/reactQuery/get';
import ProjectShareMaxRespondets from '@/components/projects/single-project/share/ProjectShareMaxRespondents';
import { artificialDelay } from '@/utils/artificialDelay';
import ToastMessage from '@/components/toast-message/ToastMessage';
import {
  hasEmptyQuestions,
  isInterviewQuestionsEmpty,
  mergeServerMediaIntoQuestionTree,
  mergeServerMediaIntoScreeningQuestions,
  pruneUntouchedEmptyGroups,
} from '@/utils/questionGroupHelpers';
import {
  pruneNoIntentScreeningAnswers,
  sanitizeScreeningQuestionsForPayload,
} from '@/utils/screeningQuestionHelpers';
import {
  getChangedProjectFields,
  shouldUploadMediaOnSave,
  PROJECT_DIFF_FIELDS,
  normalizeProjectFieldValue,
  needsInterviewQuestionsValidation,
  partitionChangedFields,
  pruneEmptySnippets,
  stripVolatileMediaFields,
} from '@/utils/projectFormDiff';
import { createProjectValidationSchema } from '@/schemas';
import {
  useProjectDraft,
  clearProjectDraft,
  readProjectDraft,
  getDraftRestoreEligibility,
  resolveDraftRestoreValue,
  ProjectDraftEnvelope,
} from '@/hooks/useProjectDraft';

// Recursively counts leaf validation messages under an RHF error node —
// used only for the "(N issues)" toast copy, so an approximate count is
// fine.
const countValidationIssues = (node: unknown): number => {
  if (!node || typeof node !== 'object') return 0;
  const record = node as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message) return 1;
  return Object.entries(record).reduce(
    (sum, [key, value]) => (key === 'ref' ? sum : sum + countValidationIssues(value)),
    0,
  );
};

// Waits for a section's `max-height` open transition to actually finish
// (or a generous fallback timeout, in case the transition never fires — the
// section may already have been open, in which case max-height doesn't
// change at all) before scrolling. Scrolling on a fixed short timeout
// against a duration-1000 CSS transition lands on the still-clipped,
// visually wrong position — see the discussion/screening section wrappers'
// `transition-all duration-1000` (TELV1-490 architect review, Point 2b-ii).
//
// Returns a cleanup function that cancels the pending listener/timeout —
// callers must invoke it on unmount so a component torn down mid-animation
// (e.g. the user navigates away right after clicking save) doesn't leave an
// orphaned `transitionend` listener + timer behind. TELV1-490 PR review
// MINOR finding.
const scrollAfterSectionTransition = (
  animatedElement: HTMLDivElement | null,
  scrollTarget: HTMLDivElement | null,
): (() => void) => {
  const scroll = () => {
    scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!animatedElement) {
    scroll();
    return () => {};
  }

  let settled = false;
  const controller = new AbortController();

  const handleTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== animatedElement || event.propertyName !== 'max-height') return;
    if (settled) return;
    settled = true;
    controller.abort();
    scroll();
  };

  animatedElement.addEventListener('transitionend', handleTransitionEnd, {
    signal: controller.signal,
  });

  // Fallback: slightly longer than the 1000ms transition, in case it never
  // fires (already open, reduced-motion, etc).
  const timeoutId = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    controller.abort();
    scroll();
  }, 1100);

  // Abort (called either by the two branches above, or by the caller's
  // unmount cleanup) always clears the timer too, so there's a single exit
  // path that can't leave either the listener or the timeout behind.
  controller.signal.addEventListener('abort', () => window.clearTimeout(timeoutId));

  return () => {
    settled = true;
    controller.abort();
  };
};

function ProjectCreate() {
  const { lang } = useContext(LangContext);
  const { currentTheme } = useTheme();
  const { pathname } = useLocation();
  // Below lg the test chat sits behind a trigger button and opens in a
  // right-side Sheet slide-over so the design form gets the full viewport
  // width. Crossing the lg breakpoint mid-test will unmount the sheet
  // contents — documented as an accepted tradeoff.
  const isLg = useMediaQuery('(min-width: 1024px)');
  const [testChatSheetOpen, setTestChatSheetOpen] = useState(false);
  const {
    project_id: projectId,
    organization_id: organizationId,
    workspace_id: workspaceId,
  } = useParams();
  const { isUserViewer } = useWorkspaceRoles();
  const { setShowError } = useErrorStore();
  const {
    isFormChanged,
    setIsFormChanged,
    isMediaReadyForUpload,
    setIsMediaReadyForUpload,
    uploadingQuestionId,
    setUploadingQuestionId,
    reorderChanged,
    setReorderChanged,
    interviewQuestionsStructureChanged,
    setInterviewQuestionsStructureChanged,
    isProjectCreateButtonDisabled,
  } = useCreateProjectStore();
  const [activeSection, setActiveSection] = useState<EProjectDesignSection | null>(
    EProjectDesignSection.INSTRUCTIONS,
  );
  const discussionGuideSectionRef = useRef<HTMLDivElement>(null);
  const screeningSectionRef = useRef<HTMLDivElement>(null);
  // Refs on the inner `max-height`-animated wrappers (not the outer section
  // refs above) — needed to listen for the animation to actually finish
  // before scrolling, see `waitForSectionOpenTransition` below. TELV1-490.
  const discussionGuideAnimatedRef = useRef<HTMLDivElement>(null);
  const screeningAnimatedRef = useRef<HTMLDivElement>(null);
  // TELV1-572 — every section used to animate against a hard-coded pixel
  // budget on an `overflow-hidden` wrapper, so anything taller was silently
  // clipped (the Screening one cut off "Add new answer" from the third
  // answer on). These measure the real content instead. The ref goes on an
  // inner wrapper, never on the element whose max-height we set.
  const [instructionsContentRef, instructionsHeight] = useMeasuredHeight();
  const [contextContentRef, contextHeight] = useMeasuredHeight();
  const [screeningContentRef, screeningHeight] = useMeasuredHeight();
  const [discussionGuideContentRef, discussionGuideHeight] = useMeasuredHeight();
  const [maxRespondentsContentRef, maxRespondentsHeight] = useMeasuredHeight();
  // Cleanup functions returned by scrollAfterSectionTransition for any
  // still-pending listener/timeout, run on unmount so a component torn down
  // mid-animation never leaks a `transitionend` listener. TELV1-490 PR
  // review MINOR finding.
  const pendingScrollCleanupsRef = useRef<Array<() => void>>([]);
  useEffect(
    () => () => {
      pendingScrollCleanupsRef.current.forEach((cleanup) => cleanup());
      pendingScrollCleanupsRef.current = [];
    },
    [],
  );
  // Bumped on every failed submit attempt so collapsed interview-question
  // groups containing an error can force themselves open. See
  // GroupContainer's force-open effect.
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const setInitialMessages = useChatStore((state) => state.setInitialMessages);
  const setCurrentConversationId = useChatStore((state) => state.setCurrentConversationId);
  const [fileData, setFileData] = useState<{ [key: string]: QuestionsFilesData }>({});
  // TELV1-491 — draft safety-net mount-detection banner. Set only by the
  // dedicated sibling effect below (never by the TELV1-400 reset effect —
  // see that effect's own comment for why it stays untouched); read only by
  // the plain, non-focus-stealing banner markup near the top of the scroll
  // container.
  const [draftBanner, setDraftBanner] = useState<{
    draft: ProjectDraftEnvelope;
    updatedElsewhere: boolean;
  } | null>(null);
  // TELV1-491 post-ship fix (destructive-restore incident) — REACTIVE
  // (state, not a ref) mirror of `lastSyncedProjectIdRef`, set at the very
  // end of the `[singleProjectData]` reset effect below, in the SAME effect
  // invocation as `reset(serverValues)`. React 18 batches both updates into
  // one re-render, so by the render where `formSyncedProjectId` first
  // matches the current project, `watch()` is ALSO guaranteed to already
  // reflect the post-reset server values — see `useProjectDraft`'s
  // `isFormReady` guard for why this matters (it closes the mount-time race
  // that let the write-effect fire on a stale, pre-reset snapshot and
  // silently destroy a real pre-existing draft).
  const [formSyncedProjectId, setFormSyncedProjectId] = useState<string | undefined>(undefined);
  const { setChatSettingsData } = useChatSettingsStore();
  const {
    data: singleProjectData,
    error: projectDataError,
    refetch,
    isLoading: isProjectDataLoading,
  } = useGetSingleProjectData(organizationId!, workspaceId!, projectId!);

  const minNumber = singleProjectData?.respondents_max;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isFormChanged && currentLocation.pathname !== nextLocation.pathname,
  );

  const handleToggleSection = (section: EProjectDesignSection) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const {
    register,
    control,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors, dirtyFields },
    clearErrors,
    trigger,
    getFieldState,
  } = useForm({
    defaultValues: {
      type_of_interview: singleProjectData?.type_of_interview || [],
      interviewer_name: singleProjectData?.interviewer_name || '',
      company_name: singleProjectData?.company_name || '',
      interview_goal: singleProjectData?.interview_goal || '',
      interview_audience: singleProjectData?.interview_audience || '',
      tone_of_voice: singleProjectData?.tone_of_voice || '',
      language: singleProjectData?.language || '',
      // `?? null` (not `|| null`): `false` is a real, meaningful value here
      // (explicitly disabled) and must not be coerced to `null` ("unset").
      // The old `|| null` silently turned every `false` into `null` on
      // load, permanently disagreeing with the reset effect below (which
      // preserves raw `false`) — a standing false-dirty source. TELV1-490.
      allow_language_selection: singleProjectData?.allow_language_selection ?? null,
      privacy_policy: singleProjectData?.privacy_policy || '',
      analysis_language:
        singleProjectData?.analysis_language !== ''
          ? singleProjectData?.analysis_language
          : 'English',
      theme_id: singleProjectData?.theme_id || '',
      snippets: singleProjectData?.snippets || [],
      opening_message: singleProjectData?.opening_message || '',
      respondents_max: singleProjectData?.respondents_max || null,
      interview_questions: singleProjectData?.interview_questions || undefined,
      screening_questions:
        singleProjectData?.screening_questions?.map((question: ScreeningQuestion) => ({
          ...question,
        })) || [],
    },

    resolver: yupResolver(
      createProjectValidationSchema(
        minNumber || 0,
        singleProjectData?.status === EProjectStatus.PUBLISHED ||
          singleProjectData?.status === EProjectStatus.COMPLETED,
      ),
    ),
    mode: 'onSubmit',
  });

  const { mutateAsync, status } = useMutateProjectUpdate(organizationId, workspaceId, projectId);
  const { getButtonText } = useFormButtonText();
  const isProjectPublished = singleProjectData?.status === EProjectStatus.PUBLISHED;
  const isProjectCompleted = singleProjectData?.status === EProjectStatus.COMPLETED;
  const currentQuestions = useWatch({ name: 'interview_questions', control });
  const isInstructionsOpen = activeSection === EProjectDesignSection.INSTRUCTIONS;
  const isContextOpen = activeSection === EProjectDesignSection.CONTEXT;
  const isDiscussionGuideOpen = activeSection === EProjectDesignSection.DISCUSSION_GUIDE;
  const isMaxRespondentsOpen = activeSection === EProjectDesignSection.MAX_RESPONDENTS;
  const isScreeningOpen = activeSection === EProjectDesignSection.SCREENING;

  const { requiredFields, setRequiredFields } = useProjectRequiredFieldsStore();

  // Watch all form values to detect changes in required fields
  const allFormValues = watch();

  const watchedValuesArray = requiredFields.map((field) => {
    const value = allFormValues[field.key as keyof typeof allFormValues];
    if (field.key === 'interview_questions') {
      // Serialize the entire interview_questions structure to detect any changes
      // This ensures changes in nested groups/questions trigger the validation
      return JSON.stringify(value || '');
    }
    return value || '';
  });
  const watchedJoinValues = watchedValuesArray.join(',');

  // Persists an already-built payload (a subset of ProjectCreationValues
  // keys) to the server and reconciles local state afterwards. Shared by
  // the full save (onSubmit) and the partial save that fires when some
  // changed fields are valid and others aren't (TELV1-490 Point 2) — both
  // need the identical mutate/media-upload/refetch sequence, differing only
  // in whether the form is fully reset to clean afterwards.
  const persistProjectPayload = async (
    payload: Record<string, unknown>,
    data: ProjectCreationValues,
    { isFullSave }: { isFullSave: boolean },
  ) => {
    try {
      await artificialDelay();

      // Blank Context rows (the form's [''] initialization, empty "add
      // context" rows) are a UI affordance, not content — never persist
      // them. Pruned here, not per call path, so the full save (onSubmit)
      // and the partial save (onError) can't diverge (TELV1-512).
      const sanitizedPayload = Array.isArray(payload.snippets)
        ? { ...payload, snippets: pruneEmptySnippets(payload.snippets) }
        : payload;

      await mutateAsync({
        _id: projectId,
        ...sanitizedPayload,
      });

      // Media only ever uploads on a FULL save — see shouldUploadMediaOnSave.
      // A partial save (some other field failed validation) must NOT upload
      // media nor clear isMediaReadyForUpload, otherwise a later full save
      // would never re-attempt the upload and it would be silently lost.
      // TELV1-490 PR review MAJOR finding.
      if (shouldUploadMediaOnSave(isFullSave, isMediaReadyForUpload)) {
        const mediaUploadPromises = Object.keys(fileData).map(async (questionId) => {
          const questionMedia = fileData[questionId].file;
          if (questionMedia) {
            setUploadingQuestionId(questionId);
            try {
              await axios.post(
                `${
                  import.meta.env.VITE_API_BASE_URL
                }/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
                { media: questionMedia },
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${Cookie.get('token')}`,
                  },
                },
              );
            } catch {
              toast.error(
                <ToastMessage
                  description="unsupportedMediaTypeDescription"
                  title="unsupportedMediaType"
                />,
                { position: 'top-center' },
              );
            } finally {
              setUploadingQuestionId(null);
            }
          }
        });

        await Promise.all(mediaUploadPromises);
      }

      if ('type_of_interview' in data) {
        setChatSettingsData(data?.type_of_interview as string[]);
      }

      // Clear change-tracking only for what was actually saved, so the Save
      // button and the unsaved-changes navigation blocker stay truthful. A
      // partial save (validation failure elsewhere, or a screening-question
      // deletion via skipValidation) must NOT clear interview-question edits
      // that are still pending — otherwise the subsequent refetch could
      // silently drop them. TELV1-400.
      if ('interview_questions' in payload) {
        setReorderChanged(false);
        setInterviewQuestionsStructureChanged(false);
      }

      if (isFullSave) {
        // Full save: everything pending — including media — was included in
        // this save, so the form (and the media-ready flag) is now clean.
        // Cleared here, after the await, so a failed save keeps the user's
        // edits + dirty state, and a partial save (isFullSave: false) never
        // reaches this branch, leaving isMediaReadyForUpload untouched for
        // the next full save to pick up. TELV1-490 PR review MAJOR finding.
        setIsMediaReadyForUpload(false);
        setIsFormChanged(false);
        // Also clear the two structural flags here, not just in the
        // payload-scoped branch above. `reorderChanged` is SHARED with the
        // screening editor's drag handler, so a screener-only save left it
        // stuck at `true` forever (its only reset was gated on
        // `interview_questions` being in the payload). A stuck flag keeps
        // the Save button enabled with nothing to send, and the next click
        // lands in onSubmit's empty-payload guard — the customer-visible
        // "Error, please try again." after a save that actually worked
        // (Sentry TELLET-DASHBOARD-4MJ: reorderChanged true, isFormChanged
        // false, empty payload). A FULL save is value-diff driven across
        // every field, so anything still flagged afterwards is stale by
        // definition. Partial saves keep the narrower rule above.
        setReorderChanged(false);
        setInterviewQuestionsStructureChanged(false);
        reset({}, { keepValues: true });
        // TELV1-491 — the one MANDATORY draft clear: a full save just
        // persisted everything the draft could have restored, so the
        // safety net is now stale by definition. Partial saves
        // (isFullSave: false) intentionally skip this whole branch and
        // must NOT clear the draft — see this file's Point 2 in the
        // TELV1-491 binding spec.
        clearProjectDraft(projectId);
      }

      await refetch();
    } catch (error) {
      const axiosError = error as AxiosError;
      const message = getErrorMessage(axiosError);
      toast.error(message);
      throw axiosError;
    }
  };

  const onSubmit = async (data: ProjectCreationValues, skipValidation: boolean = false) => {
    if (isUserViewer) return;

    // skipValidation is the DELETION-ONLY auto-save path: its sole caller is
    // ScreeningQuestions' handleDeleteQuestion, which persists the removal of
    // an already-saved question immediately, bypassing form validation (the
    // remaining questions may be mid-edit and legitimately invalid). It is a
    // partial save (isFullSave: false below), so it never clears drafts or
    // dirty state. New save flows must go through handleFormSubmit instead.
    // Prune no-intent screener answer rows here too — handleFormSubmit prunes
    // before its onSubmit call, but this path skips handleFormSubmit, so
    // without this a deletion could persist empty scratch rows that the next
    // full save would prune anyway (Release 3.5.0 audit follow-up).
    const rawPayload = skipValidation
      ? {
          ...(data.interview_questions ? { interview_questions: data.interview_questions } : {}),
          ...(data.screening_questions
            ? {
                screening_questions: pruneNoIntentScreeningAnswers(
                  data.screening_questions as ScreeningQuestion[],
                ),
              }
            : {}),
        }
      : getChangedProjectFields(data, singleProjectData);

    const screeningSanitized = rawPayload.screening_questions
      ? {
          ...rawPayload,
          screening_questions: sanitizeScreeningQuestionsForPayload(
            rawPayload.screening_questions as ScreeningQuestion[],
          ),
        }
      : rawPayload;
    // Strip per-fetch signed `url`s / runtime `File`s from the outgoing tree:
    // the backend persists interview_questions as-sent (no DTO validation on
    // this field), so without this every save writes stale signed URLs into
    // the DB.
    const payload = screeningSanitized.interview_questions
      ? {
          ...screeningSanitized,
          interview_questions: stripVolatileMediaFields(
            screeningSanitized.interview_questions,
          ) as InterviewQuestions,
        }
      : screeningSanitized;

    // Check if payload is empty
    if (Object.keys(payload).length === 0) {
      // A user-initiated save that yields no payload means our change-tracking
      // failed to capture an edit — the TELV1-400 silent-loss signature.
      // Value-diffing (getChangedProjectFields) is now the source of truth for
      // "changed", so an empty payload here means it genuinely found nothing —
      // but with imperfect normalization this still guards against a phantom
      // save, so keep the flags as a secondary signal worth flagging.
      const hasUnsavedEdits =
        isFormChanged ||
        reorderChanged ||
        interviewQuestionsStructureChanged ||
        Object.keys(dirtyFields).length > 0;
      if (hasUnsavedEdits && !skipValidation) {
        Sentry.captureMessage('TELV1-400: empty save payload despite unsaved edits', {
          level: 'warning',
          extra: {
            projectId,
            dirtyFieldKeys: Object.keys(dirtyFields),
            isFormChanged,
            reorderChanged,
            interviewQuestionsStructureChanged,
          },
        });
        // Neutral, not an error: there is nothing here for the user to
        // retry. The payload is empty because the value-diff found no
        // difference from the server, so the click genuinely had nothing to
        // send — the previous "Error, please try again." read as a failed
        // save and sent customers hunting for data loss that hadn't
        // happened. The Sentry warning above still carries the signal for
        // us, which is where an actual change-tracking miss belongs.
        toast.info(lang.get('msg.noChangesToSave'));
      }
      return;
    }

    await persistProjectPayload(payload, data, { isFullSave: !skipValidation });
  };

  // Refs let the reset effect read the latest dirty state / change flags / the
  // project it last synced, without re-running on every keystroke or capturing
  // stale closures.
  const hasInitializedForm = useRef(false);
  const lastSyncedProjectIdRef = useRef<string | undefined>(undefined);
  const dirtyFieldsRef = useRef(dirtyFields);
  dirtyFieldsRef.current = dirtyFields;
  const changeFlagsRef = useRef({
    isFormChanged,
    reorderChanged,
    interviewQuestionsStructureChanged,
  });
  changeFlagsRef.current = {
    isFormChanged,
    reorderChanged,
    interviewQuestionsStructureChanged,
  };

  useEffect(() => {
    if (!singleProjectData) return;

    const fields = [
      'type_of_interview',
      'interviewer_name',
      'company_name',
      'interview_goal',
      'interview_audience',
      'tone_of_voice',
      'privacy_policy',
      'language',
      'allow_language_selection',
      'analysis_language',
      'theme_id',
      'opening_message',
      'interview_questions',
      'screening_questions',
      'respondents_max',
    ];

    const serverValues = fields.reduce<Record<string, unknown>>((acc, key) => {
      const projectKey = key as keyof typeof singleProjectData;
      acc[key] =
        key === 'respondents_max' && singleProjectData[projectKey] === 0
          ? null
          : singleProjectData[projectKey];
      return acc;
    }, {});

    // Preserve in-progress edits ONLY when refetching the SAME project AND there
    // are genuine unsaved edits. A project switch (different id — the component
    // is reused, not remounted, across project navigation) or a clean post-save
    // refetch (no unsaved edits) does a full reset to server, which (a) syncs
    // server-canonical data such as media URLs / ids and (b) prevents one
    // project's edits leaking into another. `dirtyFields` is the reliable
    // "has edits" signal (the Zustand flags can be flipped by screening
    // handlers), so it stays in the OR. TELV1-400.
    const sameProject = lastSyncedProjectIdRef.current === singleProjectData._id;
    const flags = changeFlagsRef.current;
    const hasUnsavedEdits =
      flags.isFormChanged ||
      flags.reorderChanged ||
      flags.interviewQuestionsStructureChanged ||
      Object.keys(dirtyFieldsRef.current).length > 0;
    const preserveEdits = hasInitializedForm.current && sameProject && hasUnsavedEdits;

    // Capture the user's current edits BEFORE reset (reset overwrites them).
    const currentValues = getValues();

    // Reset to PURE server values first → clean defaults, server-canonical sync.
    reset(serverValues);

    if (preserveEdits) {
      // Re-apply the unsaved edits AFTER reset, via setValue(shouldDirty), so
      // they keep their dirty mark (value !== the just-set server default →
      // dirty → getOnlyEditedData includes them on the next save). Plain object
      // mutation + reset would drop the dirty mark and the next Save would omit
      // the edit. interview_questions is re-applied as a whole object (no
      // per-child reconciliation, which would corrupt the nested tree); scalar
      // fields use dirtyFields. TELV1-400.
      const formQuestions = currentValues.interview_questions as InterviewQuestions | undefined;
      const serverQuestions = serverValues.interview_questions as InterviewQuestions | undefined;
      // Media MEMBERSHIP is server-owned (upload/delete go through their own
      // endpoint, which triggers this very refetch): merge the server's media
      // into the preserved edits BEFORE re-applying them. Re-applying the raw
      // captured tree — a snapshot from before the upload/delete — made the
      // next save overwrite the server's media list: a fresh upload silently
      // vanished, a deletion came back as a dangling NoSuchKey reference.
      // Unsaved labels/reorder (client-owned until saved) survive the merge.
      // dequal, not JSON.stringify: the merge rebuilds questions from the
      // LOCAL object's key order, so string comparison would report a false
      // divergence whenever key order differs (e.g. a local question without
      // a media key adopting server media appends it last). The gate should
      // agree with the diff engine, which is dequal-based.
      const preservedQuestions = mergeServerMediaIntoQuestionTree(formQuestions, serverQuestions);
      const questionsDiverged =
        !isInterviewQuestionsEmpty(preservedQuestions) &&
        !dequal(preservedQuestions ?? null, serverQuestions ?? null);
      if (questionsDiverged) {
        setValue('interview_questions', preservedQuestions, { shouldDirty: true });
      }

      // Same divergence check as interview_questions above, generalized to
      // every other dirty field. Without it, a field a prior partial autosave
      // (e.g. Screening's fire-and-forget save on delete) already persisted
      // gets unconditionally re-marked dirty here even though it now matches
      // the server value — which then trips the empty-payload sentinel above
      // on the next manual save and shows a false "Error, please try again."
      // TELV1-400.
      Object.keys(dirtyFieldsRef.current).forEach((key) => {
        if (key === 'interview_questions' || !(key in serverValues)) return;

        const currentValue = currentValues[key as keyof typeof currentValues];
        const serverValue = serverValues[key as keyof typeof serverValues];
        const fieldDiverged =
          JSON.stringify(currentValue ?? null) !== JSON.stringify(serverValue ?? null);

        if (fieldDiverged) {
          setValue(key as keyof ProjectCreationValues, currentValue as never, {
            shouldDirty: true,
          });
        }
      });
    }

    if (singleProjectData.snippets?.length) {
      setValue('snippets', singleProjectData.snippets);
    } else {
      setValue('snippets', ['']);
    }

    hasInitializedForm.current = true;
    // Assigned unconditionally (incl. first load): otherwise the first
    // same-project refetch would see a stale id, treat it as a project switch,
    // full-reset, and drop pending edits — re-opening TELV1-400.
    lastSyncedProjectIdRef.current = singleProjectData._id;
    // TELV1-491 post-ship fix — see `formSyncedProjectId`'s declaration
    // comment. Set last, after `reset()`/`setValue()` above, so it becomes
    // true in the same batched re-render where `watch()` already reflects
    // the synced values.
    setFormSyncedProjectId(singleProjectData._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleProjectData]);

  useEffect(() => {
    if (
      (projectDataError as AxiosError)?.status === EStatusCode.BAD_REQUEST ||
      (projectDataError as AxiosError)?.status === EStatusCode.PROJECT_NOT_FOUND
    ) {
      setShowError(true);
    }
  }, [projectDataError, setShowError]);

  useEffect(() => {
    const handlePageLeave = () => {
      setInitialMessages([]);
      setCurrentConversationId('');
    };

    return () => {
      handlePageLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (errors?.privacy_policy?.message) {
      if (!isInstructionsOpen) {
        handleToggleSection(EProjectDesignSection.INSTRUCTIONS);

        const scrollAfterToggle = () => {
          const scrollContainer = document.querySelector('#scrollContainer');
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        };

        window.requestAnimationFrame(() => {
          setTimeout(scrollAfterToggle, 400);
        });
        toast.error('Privacy policy must be a valid link.', { position: 'top-center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isInstructionsOpen]);

  useEffect(() => {
    // Live required-field state from the in-progress form values. Uses the same
    // pure computation as the saved-data hydration in ProjectNavbar so the two
    // never disagree (TELV1-509).
    const debounceTimer = setTimeout(() => {
      setRequiredFields(computeRequiredFields(allFormValues as Record<string, unknown>));
    }, 300);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedJoinValues]);

  // TELV1-491 architect review (PR #1656) — REPAIRED. The previous
  // `[allFormValues, singleProjectData]` dependency array never actually
  // memoized anything: `allFormValues` (from `watch()`) is a new object
  // reference on EVERY render regardless of whether any field's content
  // changed, so this recomputed the deep diff (which walks the full
  // interview-question tree) on every single render — the exact cost the
  // memo was meant to avoid. TELV1-490 PR review MINOR finding's claim that
  // "the memo still prevents recomputing... on re-renders where the
  // dependency values are unchanged for other reasons" was incorrect.
  //
  // Fixed with a genuinely STABLE dependency: a string built from only the
  // PROJECT_DIFF_FIELDS keys, each normalized the same way the diff engine
  // itself compares them — content-equal renders now produce an
  // identical string (primitive equality), so the memo actually skips the
  // deep diff on renders where nothing in the compared field set changed.
  // Also File-safe: `normalizeProjectFieldValue`'s `interview_questions`
  // case strips `media` before this ever reaches `JSON.stringify`, same as
  // `useProjectDraft`'s own snapshot (`useProjectDraft.ts`).
  //
  // Declared before the `blocker.state === 'blocked'` early return below —
  // hooks can't follow a conditional return.
  const changedFieldsSnapshotKey = PROJECT_DIFF_FIELDS.map(
    (key) =>
      `${key}:${JSON.stringify(normalizeProjectFieldValue(key, (allFormValues as ProjectCreationValues)[key]))}`,
  ).join('|');
  const changedFieldsForButton = useMemo(
    () => getChangedProjectFields(allFormValues as ProjectCreationValues, singleProjectData),
    // `allFormValues` itself is intentionally NOT a dependency — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changedFieldsSnapshotKey, singleProjectData],
  );

  // TELV1-491 post-ship fix (destructive-restore incident) — see
  // `formSyncedProjectId`'s declaration comment and `UseProjectDraftGuards`
  // in useProjectDraft.ts for the full root-cause writeup. `isFormReady`
  // closes the mount-time race where the write-effect could evaluate a
  // stale, pre-reset form snapshot against the just-loaded server data and
  // silently destroy a real draft; `suppressWrites` is defense-in-depth
  // that additionally freezes the draft entirely while an un-actioned
  // restore banner is on screen for this project.
  const isFormReadyForDraft =
    !!singleProjectData?._id && formSyncedProjectId === singleProjectData._id;

  // TELV1-491 — draft safety net. A pure read-side observer: it only reads
  // `allFormValues`/`singleProjectData` and writes to localStorage, never
  // the save payload path. Also declared above the blocked-state early
  // return per the rules-of-hooks constraint noted above.
  //
  // `isUserViewer ? undefined : projectId` suppresses writes entirely for
  // read-only viewers (they cannot edit, so a draft would never be theirs
  // to restore) — the hook's own `!projectId` guard then no-ops the rest of
  // the effect.
  //
  // `suppressWrites`: an un-actioned restore banner OR a save mutation
  // currently in flight (`status === 'pending'`) — the latter is the
  // TELV1-491 architect review's PR #1656 fix: a debounce queued from an
  // edit made just before Save can otherwise fire right after the
  // mandatory `clearProjectDraft` call in `persistProjectPayload`'s
  // `isFullSave` branch, re-writing a draft compared against `serverData`
  // that hasn't caught up yet (the post-save `refetch()` hasn't resolved).
  // See `UseProjectDraftGuards.suppressWrites` in `useProjectDraft.ts` for
  // the full writeup.
  useProjectDraft(
    isUserViewer ? undefined : projectId,
    allFormValues as ProjectCreationValues,
    singleProjectData,
    { isFormReady: isFormReadyForDraft, suppressWrites: !!draftBanner || status === 'pending' },
  );

  // Sibling, mount-scoped draft-detection effect — deliberately NOT folded
  // into the `[singleProjectData]` reset effect above, which is the most
  // safety-critical code in this file (TELV1-400 preservation logic). This
  // effect only reads localStorage and sets local banner state; it never
  // calls `reset`/`setValue`. Keyed on `singleProjectData?._id` (not the
  // whole object) so it re-runs exactly once per project — on first load
  // and on an in-SPA project switch (component reused, not remounted) —
  // and never on every server refetch.
  useEffect(() => {
    if (!singleProjectData?._id || isUserViewer) {
      setDraftBanner(null);
      return;
    }

    const draft = readProjectDraft(singleProjectData._id);
    const eligibility = getDraftRestoreEligibility(draft, singleProjectData);

    if (draft && eligibility.eligible) {
      setDraftBanner({ draft, updatedElsewhere: eligibility.updatedElsewhere });
    } else {
      setDraftBanner(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleProjectData?._id]);

  // Restore: populate via the same mechanism the TELV1-400 reset effect
  // already uses for preserved edits — `interview_questions` is re-applied
  // as a WHOLE object (never per-child, which would corrupt the nested
  // tree), scalar fields individually. `shouldDirty: true` is required so
  // the value-diff Save-button enable signal and the payload builder both
  // see the restored values. Restricted to the keys that actually differ
  // from the current server state (the same set already computed for
  // banner eligibility) rather than every snapshotted key, so restoring
  // never re-marks an already-matching field as dirty.
  //
  // TELV1-491 post-ship fix (destructive-restore incident): ATOMIC and
  // NON-DESTRUCTIVE. Root cause was two-fold — (1) a mount-time race in
  // `useProjectDraft` could hand this banner a CORRUPTED draft snapshot
  // (closed by the `isFormReady`/`suppressWrites` guards above — see
  // `UseProjectDraftGuards` in `useProjectDraft.ts`), and (2) RHF's
  // internal dirty-tracking throws for some field shapes when set to
  // certain values (observed: `type_of_interview`, bound via multiple
  // `Controller` instances rather than one registered field — see
  // `Instructions.tsx` — threw when set to `undefined`), and that
  // uncaught throw silently aborted every `setValue` call after it in the
  // loop, INCLUDING `clearProjectDraft`/`setDraftBanner(null)` — so a
  // partially-applied restore was reported as "no changes" with the
  // banner still (confusingly) on screen, while the real draft in
  // `localStorage` had already been read into `draftBanner` and would be
  // gone the next time this ran. Each field is now applied independently
  // (one field's internal RHF error can't blot out the rest), and the
  // draft is only ever cleared — and the banner only ever dismissed — once
  // every field has been CONFIRMED applied. Any failure leaves the draft +
  // banner exactly as they were (so the user can retry, or explicitly
  // Discard) and surfaces a toast instead of failing silently.
  //
  // TELV1-491 architect review (PR #1656) — array-typed fields
  // (`type_of_interview`, `screening_questions`) restore via
  // `resolveDraftRestoreValue`, which falls back to `[]` instead of
  // `undefined`. Without this, a draft where the user had cleared a
  // multi-select down to nothing (normalizes to `undefined` — see
  // `ARRAY_DIFF_FIELD_KEYS` in `useProjectDraft.ts`) would hit the exact
  // RHF crash described above on EVERY restore attempt, perpetually
  // toasting `draftRestoreFailed` with no way to recover the rest of the
  // draft.
  const handleRestoreDraft = () => {
    if (!draftBanner || !singleProjectData?._id) return;

    const { values } = draftBanner.draft;
    const changedKeys = Object.keys(
      getChangedProjectFields(values as ProjectCreationValues, singleProjectData),
    ) as (keyof ProjectCreationValues)[];

    const failedKeys: string[] = [];
    changedKeys.forEach((key) => {
      try {
        const rawValue = resolveDraftRestoreValue(
          key,
          values[key as keyof typeof values],
          singleProjectData[key as keyof typeof singleProjectData],
        );
        // Media MEMBERSHIP is server-owned (see the reset effect's merge): a
        // draft is by definition a stale snapshot, so restoring its tree
        // verbatim would re-open the silent stimulus-loss bug through this
        // side door — wiping uploads / resurrecting deletions made after the
        // draft was written. Merging also re-attaches the server's fresh
        // signed urls (drafts store media without them), so previews work
        // immediately after a restore. TELV1-400 architect review F1.
        let restoreValue = rawValue;
        if (key === 'interview_questions') {
          restoreValue = mergeServerMediaIntoQuestionTree(
            rawValue as InterviewQuestions | undefined,
            singleProjectData.interview_questions,
          );
        } else if (key === 'screening_questions') {
          // Same side door for screeners: their editor's own server-sync
          // watches server data, not the form, so it does NOT heal a
          // restored stale list before a save. TELV1-400 review F1/F5.
          restoreValue = mergeServerMediaIntoScreeningQuestions(
            rawValue as ScreeningQuestion[] | undefined,
            singleProjectData.screening_questions as ScreeningQuestion[] | undefined,
          );
        }
        // TELV1-580 — never hand setValue a nullish value. RHF crashes on
        // parent paths (no `_f`), and where it does NOT crash the nullish
        // value is written into the form and can be saved over real data.
        // Skipping and recording is safe: the draft and banner both survive
        // a non-empty failedKeys, so nothing is silently lost.
        if (restoreValue === null || restoreValue === undefined) {
          throw new Error(`Refusing to restore a nullish value for ${key}`);
        }
        setValue(key, restoreValue as never, { shouldDirty: true });
      } catch (err) {
        failedKeys.push(key);
        Sentry.captureException(err, {
          extra: {
            // Ticket-agnostic: TELV1-580's nullish guard reports here too,
            // and tagging those as TELV1-491 would misfile them. PR review.
            context: 'draft restore: setValue failed for a field',
            key,
            projectId: singleProjectData._id,
          },
        });
      }
    });

    if (failedKeys.length > 0) {
      toast.error(lang.get('msg.draftRestoreFailed'));
      return;
    }

    // TELV1-580 — a restore that lands an EMPTY children array marks nothing
    // dirty (RHF's recursion visits zero registered leaves), so without this
    // the next refetch would see no unsaved edits and reset the restore away.
    // Same signal DiscussionGuide.updateFormValue already sets.
    setIsFormChanged(true);

    clearProjectDraft(singleProjectData._id);
    setDraftBanner(null);
  };

  // Discard: clear the draft, no form mutation whatsoever.
  const handleDiscardDraft = () => {
    if (!singleProjectData?._id) return;
    clearProjectDraft(singleProjectData._id);
    setDraftBanner(null);
  };

  if (blocker.state === 'blocked') return <UnsavedChangesModal blocker={blocker} />;

  // Check if any question in the group structure has empty text
  const hasEmptyQuestion = currentQuestions ? hasEmptyQuestions(currentQuestions) : false;
  const hasInterviewQuestionsError = 'interview_questions' in errors;
  const showQuestionsPanelWarning =
    (hasEmptyQuestion || hasInterviewQuestionsError) && !isDiscussionGuideOpen;

  // Check for screening questions errors
  const hasScreeningQuestionsError = 'screening_questions' in errors;
  const showScreeningPanelWarning = hasScreeningQuestionsError && !isScreeningOpen;

  // Calculate submit button disabled state. Union of BOTH change signals —
  // the Zustand flags AND the value-diff — so the button can never be more
  // conservative than the payload builder (which is value-diff-driven): if
  // either says "changed", the button must be enabled. Disagreeing with the
  // payload's own change signal is how TELV1-490 silently re-blocked saves
  // that getOnlyEditedData had already started detecting correctly.
  const hasChangedFields = Object.keys(changedFieldsForButton).length > 0;
  const isPending = status === 'pending';
  const hasNoPendingChanges =
    !hasChangedFields && !isFormChanged && !reorderChanged && !interviewQuestionsStructureChanged;
  const saveButtonIcon = getSaveButtonIcon(status, !hasNoPendingChanges);
  const hasRespondentsMaxError = Object.keys(errors).includes('respondents_max');

  const isSubmitDisabled =
    isPending ||
    isProjectCreateButtonDisabled ||
    !!uploadingQuestionId ||
    isUserViewer ||
    hasRespondentsMaxError ||
    // Same pending-changes signal as the label below, so an enabled button
    // always says "Save changes" and a disabled one never does (TELV1-607).
    hasNoPendingChanges;

  type SectionErrorKey = 'privacy_policy' | 'interview_questions' | 'screening_questions';

  // Reads fresh, section-scoped RHF errors and (a) expands + scrolls to the
  // failing section(s), (b) force-opens any collapsed interview-question
  // group whose subtree holds an error, (c) persists whatever changed
  // fields ARE valid instead of discarding the whole save (TELV1-490 Point
  // 2 — this is what stops a valid opening_message edit from being lost
  // alongside an unrelated Discussion Guide error), and (d) toasts a
  // summary naming the failing sections and issue counts.
  const onError = async (
    freshErrors: Partial<Record<SectionErrorKey, unknown>>,
    data: ProjectCreationValues,
  ) => {
    const failingSections: { label: string; count: number }[] = [];

    if (freshErrors.screening_questions) {
      failingSections.push({
        label: lang.get('msg.screening'),
        count: countValidationIssues(freshErrors.screening_questions),
      });
      if (!isScreeningOpen) handleToggleSection(EProjectDesignSection.SCREENING);
      pendingScrollCleanupsRef.current.push(
        scrollAfterSectionTransition(screeningAnimatedRef.current, screeningSectionRef.current),
      );
    }

    if (freshErrors.interview_questions) {
      failingSections.push({
        label: lang.get('msg.discussionGuide'),
        count: countValidationIssues(freshErrors.interview_questions),
      });
      if (!isDiscussionGuideOpen) handleToggleSection(EProjectDesignSection.DISCUSSION_GUIDE);
      pendingScrollCleanupsRef.current.push(
        scrollAfterSectionTransition(
          discussionGuideAnimatedRef.current,
          discussionGuideSectionRef.current,
        ),
      );
      // A collapsed group's body is unmounted, so a nested question/subgroup
      // error has no DOM node until the group forces itself open.
      setSubmitAttempt((n) => n + 1);
    }
    // privacy_policy errors keep using the dedicated effect below (it
    // already reacts correctly to `errors` state changes, unaffected by
    // this callback's stale-closure history) — not duplicated here.

    // Filter every changed key through RHF's own live field state, not just
    // the 3 section keys `freshErrors` carries. `freshErrors` only reflects
    // what `trigger()` was explicitly called with (privacy_policy,
    // interview_questions, screening_questions), so keying off it alone
    // would let a field outside that fixed union slip into the partial-save
    // payload without ever being asked "are you actually valid?". Reading
    // getFieldState per key instead makes this self-correcting: it stays
    // correct even if the set of trigger()-validated fields changes later,
    // with no separate list to keep in sync. Non-section fields are not
    // individually trigger()-ed today (by design — see PR review), so
    // getFieldState reports them clean unless that changes; they still pass
    // through the create/update schema validation inside `mutateAsync`.
    // TELV1-490 PR review MAJOR finding.
    const changedFields = getChangedProjectFields(data, singleProjectData);
    const { validSubset } = partitionChangedFields(
      changedFields,
      (key) => getFieldState(key).invalid,
    );

    // Everything that changed is invalid — e.g. the ONLY edit was a
    // user-named group left with no children. There is nothing valid to
    // persist, so do not call persistProjectPayload (no mutate, no
    // TELV1-400 sentinel — that sentinel is reserved for onSubmit's own
    // "empty payload despite real edits" case). Fall straight through to
    // the standard validation-error UX below: the section expand,
    // submitAttempt bump and scroll already ran above regardless of this
    // branch, so only the toast is left to fire.
    if (Object.keys(validSubset).length > 0) {
      const screeningSanitizedSubset = validSubset.screening_questions
        ? {
            ...validSubset,
            screening_questions: sanitizeScreeningQuestionsForPayload(
              validSubset.screening_questions as ScreeningQuestion[],
            ),
          }
        : validSubset;
      // Same outgoing-tree hygiene as onSubmit — see the comment there.
      const payload = screeningSanitizedSubset.interview_questions
        ? {
            ...screeningSanitizedSubset,
            interview_questions: stripVolatileMediaFields(
              screeningSanitizedSubset.interview_questions,
            ) as InterviewQuestions,
          }
        : screeningSanitizedSubset;

      try {
        await persistProjectPayload(payload, data, { isFullSave: false });
        if (failingSections.length > 0) {
          const sectionNames = failingSections.map((section) => section.label).join(', ');
          const issueCount = failingSections.reduce((sum, section) => sum + section.count, 0) || 1;
          toast.error(
            lang.get('msg.savedExceptSections', {
              sections: sectionNames,
              count: String(issueCount),
            }),
          );
        }
        return;
      } catch {
        // persistProjectPayload already surfaced its own error toast for
        // the partial save; fall through to the validation toast below too.
      }
    }

    if (failingSections.length > 0) {
      toast.error(lang.get('msg.fixValidationErrors'));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUserViewer) return;

    // Prune no-intent scratch state before validating: an empty screener
    // answer row nobody filled in, or an auto-scaffolded group nobody added
    // a question to, should never block (or corrupt) a save. A user-named
    // or previously-saved empty group is intentionally left alone — it gets
    // the visible validation error instead. TELV1-490 Point 4.
    const beforePrune = getValues();
    const prunedScreeningQuestions = pruneNoIntentScreeningAnswers(
      beforePrune.screening_questions as ScreeningQuestion[] | undefined,
    );
    if (prunedScreeningQuestions !== beforePrune.screening_questions) {
      setValue('screening_questions', prunedScreeningQuestions, { shouldDirty: true });
    }
    const prunedInterviewQuestions = pruneUntouchedEmptyGroups(
      beforePrune.interview_questions,
      singleProjectData?.interview_questions,
    );
    if (prunedInterviewQuestions !== beforePrune.interview_questions) {
      setValue('interview_questions', prunedInterviewQuestions, { shouldDirty: true });
    }

    const data = getValues();

    // Value-diff (not dirtyFields) decides which fields need validating —
    // dirtyFields can miss a real change (reorder, programmatic setValue),
    // which is exactly the TELV1-400 class of bug this whole PR targets.
    const changedKeys = new Set(
      Object.keys(getChangedProjectFields(data, singleProjectData)),
    ) as Set<keyof ProjectCreationValues>;

    // interview_questions is the one exception: value-diff alone can report
    // "unchanged" for it even when the user genuinely touched it (a tree
    // whose only content is a newly-created, user-named-but-childless group
    // normalizes to the same "empty" value as the server baseline). OR-ing
    // in the structural-change signals — which are NOT normalized away —
    // closes that gap. See needsInterviewQuestionsValidation's doc comment
    // and TELLET-DASHBOARD-4MJ (TELV1-490 residual case).
    const fieldsToValidate: Array<keyof ProjectCreationValues> = [
      ...(changedKeys.has('privacy_policy') ? (['privacy_policy'] as const) : []),
      ...(needsInterviewQuestionsValidation(
        changedKeys,
        interviewQuestionsStructureChanged,
        reorderChanged,
      )
        ? (['interview_questions'] as const)
        : []),
      ...(changedKeys.has('screening_questions') ? (['screening_questions'] as const) : []),
    ];

    // Invariant (TELV1-490 review): this early return is only reachable when
    // the value-diff found no section changes AND neither structure/reorder
    // flag is set — i.e. genuinely nothing needing validation. Invalid-only
    // edits (e.g. a childless named group) set the structure flag and are
    // routed through trigger()/onError above; onSubmit's empty-payload
    // sentinel still covers tracked-but-undiffable edits.
    if (!fieldsToValidate.length) {
      await onSubmit(data, false);
      return;
    }

    const isValid = await trigger(fieldsToValidate);

    if (!isValid) {
      // Read FRESH field errors via getFieldState, which is backed by RHF's
      // live internal formState — NOT the `errors` destructured at render
      // time. That render-time `errors` is still the value from BEFORE this
      // trigger() call (RHF hasn't re-rendered yet inside this same async
      // function), so on a first submit it's `{}` and the old `onError`
      // silently did nothing at all. TELV1-490 architect review, Point 2b(i).
      const freshErrors: Partial<Record<SectionErrorKey, unknown>> = {
        privacy_policy: fieldsToValidate.includes('privacy_policy')
          ? getFieldState('privacy_policy').error
          : undefined,
        interview_questions: fieldsToValidate.includes('interview_questions')
          ? getFieldState('interview_questions').error
          : undefined,
        screening_questions: fieldsToValidate.includes('screening_questions')
          ? getFieldState('screening_questions').error
          : undefined,
      };
      await onError(freshErrors, data);
      return;
    }
    await onSubmit(data, false);
  };

  return (
    <main className="flex  w-full h-full -mt-2.5 relative overflow-y-hidden max-h-[90vh]">
      <form onSubmit={handleFormSubmit} className="flex flex-col w-full lg:w-1/2 h-screen z-5">
        <div className="fixed top-14 flex items-center gap-3">
          <h2 className="pb-2 mt-3 text-xl font-semibold epilogue-font">
            {lang.get('msg.design')}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="lg:hidden mt-3"
            onClick={() => setTestChatSheetOpen(true)}
          >
            <MessageCircle className="h-4 w-4 mr-1.5" />
            {lang.get('msg.testChat')}
          </Button>
        </div>
        <div
          id="scrollContainer"
          className="z-10 fixed w-[calc(100%-32px)] lg:w-[48%] top-[105px] flex flex-col h-full gap-2 pr-4 overflow-y-auto pb-44"
        >
          {/* TELV1-491 — draft safety-net restore banner. Plain, non-modal
              markup (no Radix Dialog/AlertDialog, no autoFocus, no
              scrollIntoView, no .focus() call) so it can never steal focus
              from the UnsavedChangesModal blocker. It also never renders
              simultaneously with the blocker: the blocker's early return
              above unmounts this entire form subtree while blocked. */}
          {draftBanner && (
            <div
              role="status"
              aria-live="polite"
              className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-2">
                <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" size={16} />
                <p className="text-sm text-amber-800">
                  {draftBanner.updatedElsewhere
                    ? lang.get('msg.draftRestoreBannerTitleUpdatedElsewhere')
                    : lang.get('msg.draftRestoreBannerTitle')}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="rounded-md px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                >
                  {lang.get('msg.draftRestoreBannerDiscard')}
                </button>
                <button
                  type="button"
                  onClick={handleRestoreDraft}
                  className="rounded-md border border-amber-300 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-200 hover:border-amber-400"
                >
                  {lang.get('msg.draftRestoreBannerRestore')}
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-col w-full">
            {singleProjectData ? (
              <button
                type="button"
                className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
                onClick={() => handleToggleSection(EProjectDesignSection.INSTRUCTIONS)}
              >
                <IconSquareRoundedNumber1Filled
                  style={{ color: currentTheme?.primary }}
                  size={20}
                />
                <p>{lang.get('msg.instructions')}</p>
              </button>
            ) : (
              <div className="flex flex-col mt-2.5 mb-4">
                <div className="flex gap-1.5">
                  <Skeleton className="w-5 h-5 rounded-sm" />
                  <Skeleton className="h-5 rounded-sm w-28" />
                </div>
                <Skeleton className="w-full h-0.5 mt-2.5" />
              </div>
            )}
            {/* -- Instructions -- */}
            <div
              style={{ maxHeight: collapsibleMaxHeight(isInstructionsOpen, instructionsHeight) }}
              className="transition-all h-full ease-in-out duration-700 flex flex-col overflow-hidden -mt-1"
            >
              <div ref={instructionsContentRef}>
                <Instructions
                  singleProjectData={singleProjectData}
                  isProjectDataLoading={isProjectDataLoading}
                  isProjectPublished={isProjectPublished}
                  isProjectCompleted={isProjectCompleted}
                  register={register}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
            </div>
          </div>

          {/* -- Context -- */}
          <div className="flex flex-col w-full">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.CONTEXT)}
            >
              <IconSquareRoundedNumber2Filled style={{ color: currentTheme?.primary }} size={20} />
              {lang.get('msg.context')}
            </button>
            <div
              style={{ maxHeight: collapsibleMaxHeight(isContextOpen, contextHeight) }}
              className="transition-all ease-in-out duration-700 flex flex-col overflow-hidden -mt-1"
            >
              <div ref={contextContentRef}>
                <Context
                  isProjectPublished={singleProjectData?.status === EProjectStatus.PUBLISHED}
                  isProjectCompleted={singleProjectData?.status === EProjectStatus.COMPLETED}
                  register={register}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              </div>
            </div>
          </div>

          {/* -- Screening Questions -- */}
          <div ref={screeningSectionRef} className="flex flex-col w-full" data-section="screening">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.SCREENING)}
            >
              <IconSquareRoundedNumber3Filled style={{ color: currentTheme?.primary }} size={20} />
              <span>{lang.get('msg.screening')}</span>

              <IconAlertCircle
                size={20}
                className={`ml-auto text-red-600 transition-opacity duration-300 ease-in-out delay-300 ${
                  showScreeningPanelWarning ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </button>
            <div
              ref={screeningAnimatedRef}
              style={{ maxHeight: collapsibleMaxHeight(isScreeningOpen, screeningHeight) }}
              className="flex flex-col -mt-1 overflow-hidden transition-all duration-700 ease-in-out"
            >
              <div ref={screeningContentRef}>
                <ScreeningQuestions
                  isProjectPublished={isProjectPublished}
                  isProjectCompleted={isProjectCompleted}
                  setValue={setValue}
                  onSubmit={onSubmit}
                  screeningQuestions={singleProjectData?.screening_questions}
                  register={register}
                  errors={errors}
                  clearErrors={clearErrors}
                />
              </div>
            </div>
          </div>

          {/* -- Discussion guide -- */}
          <div
            ref={discussionGuideSectionRef}
            className="flex flex-col w-full"
            data-section="discussion-guide"
          >
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.DISCUSSION_GUIDE)}
            >
              <IconSquareRoundedNumber4Filled style={{ color: currentTheme?.primary }} size={20} />
              <span>{lang.get('msg.discussionGuide')}</span>

              <IconAlertCircle
                size={20}
                className={`ml-auto text-red-600 transition-opacity duration-300 ease-in-out delay-300 ${
                  showQuestionsPanelWarning ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </button>

            <div
              ref={discussionGuideAnimatedRef}
              style={{
                maxHeight: collapsibleMaxHeight(isDiscussionGuideOpen, discussionGuideHeight),
              }}
              className="flex flex-col -mt-1 overflow-hidden transition-all duration-700 ease-in-out"
            >
              <div ref={discussionGuideContentRef}>
                <DiscussionGuide
                  singleProjectData={singleProjectData}
                  isProjectPublished={singleProjectData?.status === EProjectStatus.PUBLISHED}
                  isProjectCompleted={singleProjectData?.status === EProjectStatus.COMPLETED}
                  register={register}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  clearErrors={clearErrors}
                  setFileData={setFileData}
                  fileData={fileData}
                  currentQuestions={currentQuestions}
                  errors={errors}
                  onSubmit={onSubmit}
                  submitAttempt={submitAttempt}
                />
              </div>
            </div>
          </div>

          {/* max respondents  */}
          <div className="flex flex-col w-full">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.MAX_RESPONDENTS)}
            >
              <IconSquareRoundedNumber5Filled style={{ color: currentTheme?.primary }} size={20} />
              {lang.get('msg.participants')}
            </button>
            <div
              style={{
                maxHeight: collapsibleMaxHeight(isMaxRespondentsOpen, maxRespondentsHeight),
              }}
              className="transition-all ease-in-out duration-700 flex flex-col overflow-hidden -mt-1"
            >
              <div ref={maxRespondentsContentRef}>
                <ProjectShareMaxRespondets
                  singleProjectData={singleProjectData}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  setIsFormChanged={setIsFormChanged}
                />
              </div>
            </div>
          </div>
          <div
            style={{ backgroundColor: currentTheme?.background }}
            className="fixed bottom-0 w-full lg:w-1/2 h-[66px] px-4 z-100 left-0"
          >
            <Button type="submit" className="w-full mt-2" disabled={isSubmitDisabled}>
              {/* TELV1-607 — label and icon come from one derived state
                  (status + any pending change), so the button can't render
                  empty when the change signals disagree. */}
              {saveButtonIcon === 'spinner' && (
                <IconLoader2 className="mr-1.5 animate-spin" size={20} />
              )}
              {saveButtonIcon === 'check' && <IconCircleCheck className="mr-1.5" size={20} />}
              {saveButtonIcon === 'save' && <IconDeviceFloppy className="mr-1.5" size={20} />}
              <span>
                {getButtonText({ status, isSubmitting: false, hasChanges: !hasNoPendingChanges })}
              </span>
            </Button>
          </div>
        </div>
      </form>

      {/* chat side — inline at lg+, Sheet slide-over below lg */}
      {isLg && (
        <div className="fixed right-0 w-1/2 h-full">
          <h2 className="mt-3 ml-8 text-xl font-semibold capitalize epilogue-font">
            {lang.get('msg.testChat')}
          </h2>
          <div
            style={{ backgroundColor: currentTheme?.background }}
            className="w-[98%] h-screen pb-20 pl-3"
          >
            {!isProjectDataLoading ? (
              <DashboardTestChat
                projectId={projectId}
                chatHeight="min-h-[calc(100dvh-190px)] max-h-[calc(100dvh-190px)]"
                singleProjectData={singleProjectData}
              />
            ) : (
              <TestChatSkeletonLoading />
            )}
          </div>
        </div>
      )}

      {/* Below lg, render the test chat in a right-side Sheet triggered
          by the button in the form header. modal={false} keeps the form
          interactive while the chat is open. */}
      {!isLg && (
        <Sheet open={testChatSheetOpen} onOpenChange={setTestChatSheetOpen} modal={false}>
          <SheetContent
            side="right"
            // TELV1-378 — shared Sheet default (z-[200]) is now above
            // fixed chrome; explicit z-[100] override is no longer
            // needed (it would actually drop the Sheet below the
            // shared default and behind the new tooltip/popover/select
            // z-[150] layer).
            className="w-full sm:w-[440px] sm:min-w-[380px] max-w-[600px] p-0 flex flex-col"
          >
            <SheetTitle className="px-5 pt-4 pb-2 text-lg font-semibold capitalize epilogue-font">
              {lang.get('msg.testChat')}
            </SheetTitle>
            <div
              style={{ backgroundColor: currentTheme?.background }}
              className="flex-1 px-4 pb-4 overflow-hidden"
            >
              {!isProjectDataLoading ? (
                <DashboardTestChat
                  projectId={projectId}
                  chatHeight="min-h-[calc(100dvh-160px)] max-h-[calc(100dvh-160px)]"
                  singleProjectData={singleProjectData}
                />
              ) : (
                <TestChatSkeletonLoading />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </main>
  );
}

export default ProjectCreate;
